/**
 * Steam store BBCode.
 *
 * This is the format the Steamworks "About This Game" box takes, and it is what a game's
 * description is stored as — verbatim, exactly as it was pasted in. Storing the source rather
 * than a conversion of it is the whole point: a description copied out of Steamworks and back
 * again is byte identical, and a tag this file has no renderer for still survives the trip,
 * because nothing ever rewrote it to save it.
 *
 * So this module only ever reads. There is no serialiser here and there should not be one: the
 * way to get the BBCode back out is to read the column.
 *
 * The tag set is Steam's store set. Anything outside it is left as the literal text it was,
 * which is also what Steam does, so a typo shows up as a typo rather than vanishing.
 */

/* --------------------------------------------------------------------- tags */

export const BB_TAGS = [
  'b',
  'i',
  'u',
  'strike',
  'spoiler',
  'noparse',
  'h1',
  'h2',
  'h3',
  'url',
  'img',
  'previewyoutube',
  'dynamiclink',
  'list',
  'olist',
  '*',
  'quote',
  'code',
  'hr',
  'table',
  'tr',
  'th',
  'td',
] as const;
export type BbTag = (typeof BB_TAGS)[number];

interface BbTagSpec {
  /**
   * `raw` contents are never parsed: what is between the tags is one run of text. `[noparse]`
   * exists to say so, `[code]` is printed as typed, and `[img]` wraps a URL rather than markup.
   */
  raw?: boolean;
  /** No closing tag and no children. A stray `[/hr]` is dropped rather than shown. */
  void?: boolean;
  /**
   * Tags this one may appear inside. A `[td]` loose in a paragraph is a mistake, and showing it
   * as the literal text it was is more use than rendering a stray table cell.
   */
  within?: readonly BbTag[];
  /**
   * True for `[*]`, the one tag with no closing form: each one runs until the next `[*]` or
   * until the list ends, so opening one closes the item before it.
   */
  closesSelf?: boolean;
}

const SPECS: Record<BbTag, BbTagSpec> = {
  b: {},
  i: {},
  u: {},
  strike: {},
  spoiler: {},
  noparse: { raw: true },
  h1: {},
  h2: {},
  h3: {},
  url: {},
  img: { raw: true },
  previewyoutube: { raw: true },
  dynamiclink: { raw: true },
  list: {},
  olist: {},
  '*': { within: ['list', 'olist'], closesSelf: true },
  quote: {},
  code: { raw: true },
  hr: { void: true },
  table: {},
  tr: { within: ['table'] },
  th: { within: ['tr'] },
  td: { within: ['tr'] },
};

const IS_TAG = new Set<string>(BB_TAGS);

/* -------------------------------------------------------------------- nodes */

export interface BbText {
  type: 'text';
  text: string;
}

export interface BbElement {
  type: 'element';
  tag: BbTag;
  /**
   * The value on the opening tag, where there is one: the target of a `[url=…]`, the name on a
   * `[quote=…]`, the `href` of a `[dynamiclink]`. Null when the tag was written bare.
   */
  attr: string | null;
  children: BbNode[];
}

export type BbNode = BbText | BbElement;

/* ------------------------------------------------------------------- parsing */

/**
 * Every `[tag]`, `[/tag]`, `[tag=value]` and `[tag key="value"]` in one pattern.
 *
 * Group 3 is whatever followed the name, kept raw so the two attribute spellings Steam uses can
 * be told apart afterwards. It cannot contain a `]`, which is what stops a line of prose with a
 * bracket in it from swallowing the rest of the description looking for a close.
 */
const TOKEN = /\[(\/?)([a-zA-Z0-9*]+)([^\]]*)\]/g;

/**
 * The value on an opening tag.
 *
 * Steam writes this two ways and both have to be read: `[url=https://…]` and `[quote=name]` put
 * the value straight after an `=`, while `[dynamiclink href="…"]` and `[table equalcells=1]`
 * write it as a named pair. Anything else after the name means this was not a tag at all, and
 * returning null is how the caller is told to leave it as the text it was.
 */
function readAttr(raw: string): { attr: string | null } | null {
  if (raw === '') return { attr: null };
  if (raw.startsWith('=')) return { attr: raw.slice(1).trim() || null };
  const pair =
    raw.match(/^\s+[a-zA-Z-]+=\s*"([^"]*)"\s*$/) ?? raw.match(/^\s+[a-zA-Z-]+=\s*([^\s"]+)\s*$/);
  return pair ? { attr: pair[1]!.trim() || null } : null;
}

/** Adds text to a node list, merging it into the run before it so adjacent text is one node. */
function pushText(into: BbNode[], text: string): void {
  if (text === '') return;
  const last = into[into.length - 1];
  if (last?.type === 'text') last.text += text;
  else into.push({ type: 'text', text });
}

/**
 * Reads a description into a tree.
 *
 * Forgiving in every direction, because the source is prose someone typed into Steamworks and a
 * store page that half renders is worse than one that renders the mistake plainly:
 *
 *   * A tag this file does not know is the text it was written as.
 *   * A close with nothing open to match is the text it was written as.
 *   * `[b]a[i]b[/b]c[/i]` closes the `[i]` along with the `[b]`, rather than dropping either.
 *   * A tag left open at the end closes there.
 */
export function parseBbcode(source: string): BbNode[] {
  const root: BbNode[] = [];
  const stack: BbElement[] = [];
  const top = (): BbNode[] => stack[stack.length - 1]?.children ?? root;

  /**
   * Tags that were closed for someone else, waiting for their own closing tag to turn up.
   *
   * `[b]a[i]b[/b]c[/i]` closes the `[i]` along with the `[b]`, which leaves a `[/i]` further on
   * with nothing open to match. Without this it would be drawn as the text `[/i]`, and prose
   * that renders correctly on Steam would come out with a stray tag in it here. So an implicit
   * close is remembered, and the real one is swallowed when it arrives.
   */
  const orphaned: BbTag[] = [];

  // Where the last tag ended. Everything between here and the next one is text, which is what
  // makes an unrecognised tag cost nothing: the loop skips it and the cursor sweeps it up.
  let cursor = 0;
  TOKEN.lastIndex = 0;

  for (let match = TOKEN.exec(source); match; match = TOKEN.exec(source)) {
    const whole = match[0];
    const closing = match[1] === '/';
    const tag = match[2]!.toLowerCase() as BbTag;
    const rest = match[3]!;

    if (!IS_TAG.has(tag)) continue;
    const parsed = readAttr(rest);
    if (!parsed) continue;

    const spec = SPECS[tag];

    if (closing) {
      if (spec.void) {
        // `[hr][/hr]` is how Steam writes a rule. The opening half already drew it, so this
        // half is swallowed rather than shown.
        pushText(top(), source.slice(cursor, match.index));
        cursor = match.index + whole.length;
        continue;
      }
      const depth = stack.map((e) => e.tag).lastIndexOf(tag);
      if (depth === -1) {
        // Nothing open to close. Either this tag was already closed on someone else's behalf,
        // in which case it is spoken for, or it is a stray and belongs on the page as text.
        const waiting = orphaned.lastIndexOf(tag);
        if (waiting === -1) continue;
        orphaned.splice(waiting, 1);
        pushText(top(), source.slice(cursor, match.index));
        cursor = match.index + whole.length;
        continue;
      }
      pushText(top(), source.slice(cursor, match.index));
      // Truncating the stack is what closes any tag opened inside this one and left open. Each
      // of those still has a closing tag somewhere ahead of it, so they are remembered.
      for (const inner of stack.slice(depth + 1)) orphaned.push(inner.tag);
      stack.length = depth;
      cursor = match.index + whole.length;
      continue;
    }

    // `[*]` has no closing form, so the item before it ends where this one starts. Worked out
    // before anything is changed, because the parent this tag has to be valid inside is the one
    // above the sibling and not the sibling itself.
    const closesSibling = spec.closesSelf === true && stack[stack.length - 1]?.tag === tag;
    const parent = stack[stack.length - (closesSibling ? 2 : 1)]?.tag;

    // A cell outside a row, an item outside a list. Only on the opening tag: a `[/th]` is read
    // against the `[th]` it closes, which is never a `[tr]`. Checked against the tag directly
    // above, so a `[td]` wrapped in a `[b]` inside a `[tr]` is still refused — the same rule
    // the renderer needs, since it draws a row's children as cells.
    if (spec.within && !spec.within.includes(parent as BbTag)) continue;

    // The text runs up to here, and it belongs to whatever is still open — which for a second
    // `[*]` is the item before it, so this has to happen before that item is closed.
    pushText(top(), source.slice(cursor, match.index));
    cursor = match.index + whole.length;
    if (closesSibling) stack.pop();

    const element: BbElement = { type: 'element', tag, attr: parsed.attr, children: [] };
    top().push(element);
    if (spec.void) continue;

    if (spec.raw) {
      // Everything up to the matching close is one run of text, tags and all. Without this,
      // `[noparse]` could not say the one thing it exists to say. An unclosed raw tag runs to
      // the end rather than eating the close of something else.
      const close = source.toLowerCase().indexOf(`[/${tag}]`, cursor);
      const end = close === -1 ? source.length : close;
      pushText(element.children, source.slice(cursor, end));
      cursor = close === -1 ? end : end + tag.length + 3;
      TOKEN.lastIndex = cursor;
      continue;
    }

    stack.push(element);
  }

  pushText(top(), source.slice(cursor));
  return root;
}

/* ------------------------------------------------------------------- helpers */

/**
 * A link target that is safe to put in an `href` or a `src`.
 *
 * A description is written by an admin, but it is also the one field on a game that is pasted
 * in wholesale from somewhere else, so `javascript:` and `data:` are refused here rather than
 * trusted never to appear. Anything that is not an absolute http(s) address comes back null,
 * and the renderer draws the text with no link on it.
 *
 * An allowlist and not a filter: the value has to start with `http://` or `https://` and carry
 * no whitespace, so what comes back can only ever be a navigation. Written as a pattern rather
 * than with `URL` because this package compiles without the DOM library — see `stores.ts`.
 */
export function safeUrl(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  return /^https?:\/\/[^\s]+$/i.test(value) ? value : null;
}

/**
 * The video id out of a `[previewyoutube=…]`.
 *
 * Steam writes the id on its own or followed by `;full`, which is a hint about how large to
 * draw the player and not part of the id.
 */
export function youtubeId(attr: string | null): string | null {
  const id = (attr ?? '').split(';')[0]?.trim();
  return id && /^[\w-]{6,20}$/.test(id) ? id : null;
}

/**
 * The description as plain words.
 *
 * For the places that want the text without the markup — a list preview, a search index — and
 * not for rendering, which walks the tree instead.
 */
export function bbcodeToText(nodes: BbNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'text') return node.text;
      if (node.tag === 'img' || node.tag === 'previewyoutube') return '';
      if (node.tag === 'hr') return '\n';
      return bbcodeToText(node.children);
    })
    .join('');
}
